import { storageService } from "@/services/firebase/storage.service"
import { firestoreService } from "@/services/firebase/firestore.service"
import { createAsyncThunk, createAction } from "@reduxjs/toolkit"
import { Document, FolderFileEntry, FolderUploadResult } from "@/types/document.types"
import { RootState } from "@/app/store"
import { formatFolderPath, formatFileSize } from "@/pages/documents/docHelper"
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

dayjs.extend(utc)
dayjs.extend(timezone)

export interface FilterParams {
  name?: string
  tag?: string
  date?: string
  editor?: string
}

// Async thunk to fetch all documents from Firestore
export const fetchDocuments = createAsyncThunk(
  'documents/fetchDocuments',
  async (_, { rejectWithValue }) => {
    try {
      const docs = await firestoreService.getDocuments('idocuments') as Document[]
      return docs
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch documents')
    }
  }
)

// Async thunk to fetch documents with filters
export const fetchDocumentsWithFilters = createAsyncThunk(
  'documents/fetchDocumentsWithFilters',
  async (filters: FilterParams, { rejectWithValue }) => {
    try {
      
      const docs = await firestoreService.getDocumentsWithFilters('idocuments', filters) as Document[]
      
      return docs
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch documents with filters')
    }
  }
)

// Async thunk to get a download URL from Firebase Storage using a storage path
export const fetchDocumentUrl = createAsyncThunk(
  'documents/fetchDocumentUrl',
  async ({storagePath }: {storagePath: string}, { rejectWithValue }) => {
    try {
      // Build full storage path (documents/ + tag/filename)
      const fullPath = `documents/${storagePath}`
      const downloadUrl = await storageService.getFileURL({path: fullPath})
      return downloadUrl
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch document URL')
    }
  }
)

export interface UploadFileParams {
  file: File
  documentName: string
  tag: string
  documentUrl: string
}

export interface UploadFileResponse extends Document {
  localId: string
}

export const uploadDocument = createAsyncThunk<
  UploadFileResponse,
  UploadFileParams,
  { rejectValue: string; state: RootState }
>(
  'documents/uploadDocument',
  async ({ file, documentName, tag, documentUrl }, { rejectWithValue, getState }) => {
    try {
      // Validations
      if (!file) {
        return rejectWithValue('No file provided')
      }
      if (!documentName.trim()) {
        return rejectWithValue('Document name is required')
      }
      if (!tag.trim()) {
        return rejectWithValue('Tag is required')
      }

      // Build full storage path (documents/ + tag/filename)
      const storagePath = `documents/${documentUrl}`

      // Check if file already exists in storage
      const fileExists = await storageService.fileExists(storagePath)
      if (fileExists) {
        return rejectWithValue('Ya existe un documento con este nombre en la carpeta de documentos. Por favor, use un nombre diferente.')
      }

      // Upload file to Firebase Storage
      await storageService.uploadFile(storagePath, file)

      // Get current user from auth state
      const state = getState()
      const currentUser = state.auth.authUser
      const lastEditor = currentUser?.displayName || currentUser?.email || 'Usuario Desconocido'

      // Prepare document metadata with Colombian timezone
      const now = dayjs().tz('America/Bogota').format('DD/MM/YYYY HH:mm')

      const newDocument: UploadFileResponse = {
        url: documentUrl, // Store only tag/filename, not full path
        name: documentName.trim(),
        tag,
        fileSize: `${(file.size / 1024).toFixed(2)}kb`,
        lastEdition: now,
        lastEditor: lastEditor,
        fileType: file.type,
        localId: Date.now().toString(),
      }

      // Save document metadata to Firestore
      const docId = (await firestoreService.addDocument('idocuments', newDocument)) as string

      return {
        ...newDocument,
        id: docId,
      }
    } catch (error: any) {
      console.error('Error uploading document:', error)
      return rejectWithValue(
        error.message || 'Failed to upload document. Please try again.'
      )
    }
  }
)

// Delete document thunk - deletes both from Storage and Firestore
export interface DeleteDocumentParams {
  documentId: string
  storagePath: string
}

export const deleteDocument = createAsyncThunk<
  string,
  DeleteDocumentParams,
  { rejectValue: string }
>(
  'documents/deleteDocument',
  async ({ documentId, storagePath }, { rejectWithValue }) => {
    try {
      console.log('Starting document deletion:', { documentId, storagePath })

      // Validations
      if (!documentId) {
        return rejectWithValue('El ID del documento es requerido')
      }
      if (!storagePath) {
        return rejectWithValue('La ruta de almacenamiento es requerida')
      }

      // Delete document metadata from Firestore first
      console.log('Deleting from Firestore collection: idocuments, id:', documentId)
      await firestoreService.deleteDocument('idocuments', documentId)
      console.log('Successfully deleted from Firestore')

      // Then delete file from Firebase Storage
      try {
        // Build full storage path (documents/ + tag/filename)
        const fullStoragePath = `documents/${storagePath}`
        console.log('Deleting from Storage:', fullStoragePath)
        await storageService.deleteFile(fullStoragePath)
        console.log('Successfully deleted from Storage')
      } catch (storageError: any) {
        // Log storage error but don't fail if file doesn't exist
        console.warn('Storage file deletion warning:', storageError)
        if (storageError.code !== 'storage/object-not-found') {
          throw storageError
        }
      }

      console.log('Document deletion completed successfully')
      return documentId
    } catch (error: any) {
      console.error('Error deleting document:', error)
      return rejectWithValue(
        error.message || 'Error al eliminar el documento. Por favor, intente nuevamente.'
      )
    }
  }
)

// Folder upload progress action (standalone to avoid circular dependency with slice)
export const updateFolderUploadProgress = createAction<{
  currentFileIndex: number
  fileName: string
  relativePath: string
  status: 'pending' | 'uploading' | 'success' | 'error'
  progress: number
}>('documents/updateFolderUploadProgress')

export const clearFolderUploadState = createAction('documents/clearFolderUploadState')

// Upload folder thunk
export interface UploadFolderParams {
  files: FolderFileEntry[]
  tag: string
}

export const uploadFolder = createAsyncThunk<
  FolderUploadResult,
  UploadFolderParams,
  { rejectValue: string; state: RootState }
>(
  'documents/uploadFolder',
  async ({ files, tag }, { rejectWithValue, getState, dispatch }) => {
    try {
      if (!files.length) {
        return rejectWithValue('No se proporcionaron archivos')
      }
      if (!tag.trim()) {
        return rejectWithValue('La etiqueta es requerida')
      }

      const state = getState()
      const currentUser = state.auth.authUser
      const lastEditor = currentUser?.displayName || currentUser?.email || 'Usuario Desconocido'
      const now = dayjs().tz('America/Bogota').format('DD/MM/YYYY HH:mm')

      const successfulUploads: Document[] = []
      const failedUploads: Array<{ fileName: string; relativePath: string; error: string }> = []

      for (let i = 0; i < files.length; i++) {
        const entry = files[i]
        const formattedPath = formatFolderPath(entry.relativePath)
        const documentUrl = `${tag}/${formattedPath}`
        const storagePath = `documents/${documentUrl}`

        dispatch(updateFolderUploadProgress({
          currentFileIndex: i,
          fileName: entry.file.name,
          relativePath: entry.relativePath,
          status: 'uploading',
          progress: 0,
        }))

        try {
          const exists = await storageService.fileExists(storagePath)
          if (exists) {
            failedUploads.push({
              fileName: entry.file.name,
              relativePath: entry.relativePath,
              error: 'Ya existe un archivo con esta ruta',
            })
            dispatch(updateFolderUploadProgress({
              currentFileIndex: i,
              fileName: entry.file.name,
              relativePath: entry.relativePath,
              status: 'error',
              progress: 0,
            }))
            continue
          }

          await storageService.uploadFileWithProgress(
            storagePath,
            entry.file,
            (progress) => {
              dispatch(updateFolderUploadProgress({
                currentFileIndex: i,
                fileName: entry.file.name,
                relativePath: entry.relativePath,
                status: 'uploading',
                progress,
              }))
            }
          )

          const docName = entry.file.name.replace(/\.[^/.]+$/, '')
          const newDocument: any = {
            url: documentUrl,
            name: docName,
            tag,
            fileSize: formatFileSize(entry.file.size),
            lastEdition: now,
            lastEditor,
            fileType: entry.file.type,
          }

          const docId = await firestoreService.addDocument('idocuments', newDocument) as string
          successfulUploads.push({ ...newDocument, id: docId })

          dispatch(updateFolderUploadProgress({
            currentFileIndex: i,
            fileName: entry.file.name,
            relativePath: entry.relativePath,
            status: 'success',
            progress: 100,
          }))
        } catch (error: any) {
          failedUploads.push({
            fileName: entry.file.name,
            relativePath: entry.relativePath,
            error: error.message || 'Error al subir archivo',
          })
          dispatch(updateFolderUploadProgress({
            currentFileIndex: i,
            fileName: entry.file.name,
            relativePath: entry.relativePath,
            status: 'error',
            progress: 0,
          }))
        }
      }

      return { successfulUploads, failedUploads, totalFiles: files.length }
    } catch (error: any) {
      return rejectWithValue(error.message || 'Error al subir la carpeta')
    }
  }
)

// Delete folder thunk - deletes all documents in a folder
export interface DeleteFolderParams {
  documents: Document[]
  folderName: string
}

export const deleteFolder = createAsyncThunk<
  string[],
  DeleteFolderParams,
  { rejectValue: string }
>(
  'documents/deleteFolder',
  async ({ documents: docs, folderName }, { rejectWithValue }) => {
    try {
      if (!docs.length) {
        return rejectWithValue('No hay documentos en la carpeta')
      }

      const deletedIds: string[] = []
      const errors: string[] = []

      for (const doc of docs) {
        if (!doc.id) continue

        try {
          await firestoreService.deleteDocument('idocuments', doc.id)

          try {
            const fullStoragePath = `documents/${doc.url}`
            await storageService.deleteFile(fullStoragePath)
          } catch (storageError: any) {
            if (storageError.code !== 'storage/object-not-found') {
              console.warn('Storage file deletion warning:', storageError)
            }
          }

          deletedIds.push(doc.id)
        } catch (error: any) {
          errors.push(`${doc.name}: ${error.message || 'Error al eliminar'}`)
        }
      }

      if (deletedIds.length === 0) {
        return rejectWithValue(`No se pudo eliminar ningún archivo de la carpeta "${folderName}"`)
      }

      if (errors.length > 0) {
        console.warn(`Errores parciales al eliminar carpeta "${folderName}":`, errors)
      }

      return deletedIds
    } catch (error: any) {
      return rejectWithValue(
        error.message || 'Error al eliminar la carpeta. Por favor, intente nuevamente.'
      )
    }
  }
)